import { useState, useEffect } from 'react'
import axios from '@fs/zion-axios'
import { i18n } from '@fs/zion-locale'

// Store for dynamic query parameters
let dynamicQueryParams = ''
let queryParamsVersion = 0
const listeners = new Set()

export function setDynamicQueryParams(params) {
  dynamicQueryParams = params
  queryParamsVersion += 1
  // Notify all listeners that the query params have changed
  listeners.forEach((listener) => listener(queryParamsVersion))
}

export default function useDynamicNavigation(dynamicLinksOn) {
  const [state, setState] = useState({ isLoading: true, isError: false })
  const [queryVersion, setQueryVersion] = useState(queryParamsVersion)

  // If language changes we need to refresh the navigation - needs to run on every render to catch changes
  const [language, setLanguage] = useState(i18n.language)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (language !== i18n.language) {
      setLanguage(i18n.language)
    }
  })

  // Subscribe to query params changes
  useEffect(() => {
    const listener = (newVersion) => {
      setQueryVersion(newVersion)
    }
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  useEffect(() => {
    let mounted = true
    if (!dynamicLinksOn) {
      setState({ isLoading: false, isError: false })
    } else {
      setState({ isLoading: true, isError: false })
      getDynamicLinks()
        .then(({ header, account: accountArray }) => {
          if (mounted) {
            let account = null
            if (accountArray?.length) {
              account = accountArray[0]
            }
            setState({ isLoading: false, isError: false, headerNavigation: header, accountNavigation: account })
          }
        })
        .catch((error) => {
          console.error(error)
          if (mounted) {
            setState({ isLoading: false, isError: true })
          }
        })
    }
    return () => {
      mounted = false
    }
    // language only to trigger a refresh when the language changes
  }, [dynamicLinksOn, queryVersion, language])
  return state
}

// exported for tests
// Helper: Read local flag overrides from localStorage
function getLocalFlagOverrides() {
  if (typeof window === 'undefined') return {}
  const raw = window.localStorage?.getItem('fsZionFlags')
  if (!raw) return {}
  try {
    return JSON.parse(raw) || {}
  } catch (e) {
    throw new Error(`Invalid JSON in localStorage key "fsZionFlags": ${e.message}`)
  }
}

// Helper: Convert overrides to CSV: flag1,flag2,!flag3
function buildForceFeatureFlagsCsv(overrides) {
  if (!overrides || typeof overrides !== 'object') return ''
  const entries = Object.entries(overrides)
  if (!entries.length) return ''
  return entries
    .map(([name, override]) => {
      const treatment = (override?.treatment || '').toString().toLowerCase()
      return treatment === 'off' ? `!${name}` : name
    })
    .filter(Boolean)
    .join(',')
}

// Helper: Merge existing & override flags preserving explicit existing tokens
function mergeForceFeatureFlags(params, overridesCsv) {
  if (!overridesCsv) return params
  const existing = params.get('forceFeatureFlags') || ''
  if (!existing) {
    params.set('forceFeatureFlags', overridesCsv)
    return params
  }
  const toTokenMap = (csv) =>
    csv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .reduce((acc, token) => {
        const base = token.replace(/^!/, '')
        if (!acc.has(base)) acc.set(base, token)
        return acc
      }, new Map())
  const existingMap = toTokenMap(existing)
  const overrideTokens = overridesCsv
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  overrideTokens.forEach((token) => {
    const base = token.replace(/^!/, '')
    if (!existingMap.has(base)) existingMap.set(base, token)
  })
  params.set('forceFeatureFlags', Array.from(existingMap.values()).join(','))
  return params
}

export function getDynamicLinks() {
  const baseUrl = `/service/home/utils/chef/header?lang=${i18n.language}`
  const params = new URLSearchParams(dynamicQueryParams || '')
  const overridesCsv = buildForceFeatureFlagsCsv(getLocalFlagOverrides())
  const finalParams = mergeForceFeatureFlags(params, overridesCsv)
  const qp = finalParams.toString()
  const url = qp ? `${baseUrl}&${qp}` : baseUrl
  return axios.get(url).then((response) => response.data)
}
